/**
 * Identity utilities for browser client
 * Captures click IDs from URL parameters and persists them
 */

import { getCookie, setCookie, COOKIE_NAMES, type CookieOptions } from './cookies';
import { getStorageItem, setStorageItem } from './storage';

export interface ClickIds {
  fbclid?: string;
  fbc?: string;
  ttclid?: string;
  ttp?: string;
  gclid?: string;
  epik?: string;
  msclkid?: string;
}

const DEFAULT_CLICK_ID_MAX_AGE_DAYS = 90;

// ttp/fbp/ga/criteo are written by external pixel scripts, not by us, so they
// don't get mirrored to localStorage.
const MIRRORED_FIELDS = {
  fbc: COOKIE_NAMES.FBC,
  ttclid: COOKIE_NAMES.TTCLID,
  gclid: COOKIE_NAMES.GCLID,
  epik: COOKIE_NAMES.EPIK,
  msclkid: COOKIE_NAMES.MSCLKID,
} as const;
type MirroredField = keyof typeof MIRRORED_FIELDS;

/**
 * Extract click IDs from URL search parameters
 */
export function extractClickIdsFromUrl(url?: string): ClickIds {
  if (typeof window === 'undefined') return {};

  const searchParams = new URLSearchParams(url ? new URL(url).search : window.location.search);

  const result: ClickIds = {};

  const fbclid = searchParams.get('fbclid');
  if (fbclid) result.fbclid = fbclid;

  const ttclid = searchParams.get('ttclid');
  if (ttclid) result.ttclid = ttclid;

  const gclid = searchParams.get('gclid');
  if (gclid) result.gclid = gclid;

  const epik = searchParams.get('epik');
  if (epik) result.epik = epik;

  const msclkid = searchParams.get('msclkid');
  if (msclkid) result.msclkid = msclkid;

  return result;
}

/**
 * Build Meta's fbc parameter from fbclid
 * Format: fb.{version}.{creation_time}.{fbclid}
 * @see https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/fbp-and-fbc
 */
export function buildFbc(fbclid: string): string {
  const version = 1;
  const creationTime = Date.now();
  return `fb.${version}.${creationTime}.${fbclid}`;
}

/**
 * Persist click IDs to cookies (and optionally to localStorage)
 * Only stores click IDs that aren't already stored
 */
export function persistClickIds(
  clickIds: ClickIds,
  options?: CookieOptions,
  persistToLocalStorage = false,
): void {
  const cookieOptions: CookieOptions = {
    maxAgeDays: DEFAULT_CLICK_ID_MAX_AGE_DAYS,
    ...options,
  };

  // Meta: fbclid -> fbc cookie
  if (clickIds.fbclid) {
    const existingFbc = getCookie(COOKIE_NAMES.FBC);
    // Only update if no existing fbc or if it's for a different fbclid
    if (!existingFbc || !existingFbc.endsWith(clickIds.fbclid)) {
      const fbc = buildFbc(clickIds.fbclid);
      setCookie(COOKIE_NAMES.FBC, fbc, cookieOptions);
    }
  }

  // TikTok: ttclid -> _ttclid cookie
  if (clickIds.ttclid && !getCookie(COOKIE_NAMES.TTCLID)) {
    setCookie(COOKIE_NAMES.TTCLID, clickIds.ttclid, cookieOptions);
  }

  // Google: gclid -> _gclid cookie
  // For accurate Google Ads tracking we need the most recent gclid to attribute conversions.
  // We only setCookie when `_gclid` is outdate, preventing unnecessary `document.cookie` updates
  if (clickIds.gclid && clickIds.gclid !== getCookie(COOKIE_NAMES.GCLID)) {
    setCookie(COOKIE_NAMES.GCLID, clickIds.gclid, cookieOptions);
  }

  // Pinterest: epik -> _epik cookie
  if (clickIds.epik && !getCookie(COOKIE_NAMES.EPIK)) {
    setCookie(COOKIE_NAMES.EPIK, clickIds.epik, cookieOptions);
  }

  // Microsoft/Bing: msclkid -> _msclkid cookie
  if (clickIds.msclkid && !getCookie(COOKIE_NAMES.MSCLKID)) {
    setCookie(COOKIE_NAMES.MSCLKID, clickIds.msclkid, cookieOptions);
  }

  if (persistToLocalStorage) {
    // Sync after cookie writes so pre-existing cookies are picked up on first opt-in.
    const maxAgeDays = cookieOptions.maxAgeDays ?? DEFAULT_CLICK_ID_MAX_AGE_DAYS;
    for (const cookieName of Object.values(MIRRORED_FIELDS)) {
      const cookieValue = getCookie(cookieName);
      if (cookieValue && getStorageItem(cookieName) !== cookieValue) {
        setStorageItem(cookieName, cookieValue, maxAgeDays);
      }
    }
  }
}

/**
 * Read all click IDs from cookies
 */
export function readClickIdsFromCookies(): ClickIds {
  return {
    fbc: getCookie(COOKIE_NAMES.FBC),
    fbclid: undefined, // fbclid is only in URL, fbc is the cookie
    ttclid: getCookie(COOKIE_NAMES.TTCLID),
    ttp: getCookie(COOKIE_NAMES.TTP),
    gclid: getCookie(COOKIE_NAMES.GCLID),
    epik: getCookie(COOKIE_NAMES.EPIK),
    msclkid: getCookie(COOKIE_NAMES.MSCLKID),
  };
}

/**
 * Read all click IDs. With `useLocalStorage`, cookies remain the source of
 * truth and localStorage is consulted only when a cookie is empty.
 */
export function readClickIds(useLocalStorage = false): ClickIds {
  const fromCookies = readClickIdsFromCookies();
  if (!useLocalStorage) {
    return fromCookies;
  }

  // Per-field guard rather than `??` — `??` would evaluate getStorageItem even
  // on a cookie hit, defeating the steady-state zero-read path.
  const merged: ClickIds = { ...fromCookies };
  for (const [field, cookieName] of Object.entries(MIRRORED_FIELDS) as [MirroredField, string][]) {
    if (merged[field] === undefined) {
      merged[field] = getStorageItem(cookieName);
    }
  }
  return merged;
}

/**
 * Capture click IDs from current URL and persist to cookies
 * (and localStorage when opted in).
 */
export function captureClickIds(
  options?: CookieOptions,
  persistToLocalStorage = false,
): ClickIds {
  const urlClickIds = extractClickIdsFromUrl();
  persistClickIds(urlClickIds, options, persistToLocalStorage);
  return readClickIds(persistToLocalStorage);
}
